'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { type PortableTextBlock } from 'next-sanity'

import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'

import { type DemoSource, submitDemoRequest } from '@/app/actions/submitDemoRequest'
import { Button, buttonSizeClasses } from '@/app/components/Button'
import { DemoRequestProps } from '@/app/components/DemoRequest/DemoRequest'
import { Input, RadioGroup, Select } from '@/app/components/Form'
import CustomPortableText from '@/app/components/PortableText'
import { trackSegmentEvent } from '@/app/lib/segment'
import { dataLayerPush } from '@/app/utils/analytics'
import { TRACKING_LOCATIONS, TrackingLocation, TrackingLocationValue } from '@/app/utils/analytics'
import { getStoredParams } from '@/app/utils/attribution'
import { isProductionDomain } from '@/app/utils/captcha'
import { isCompanyEmail } from '@/app/utils/email'
import { cn } from '@/app/utils/style'

export type DemoUserType = 'advertiser' | 'publisher'

export interface DemoRequestFormProps {
  tracking: TrackingLocation
  className?: string
  displayMode: DemoRequestProps['displayMode']
  intro?: PortableTextBlock[]
  hideFormHeader?: boolean
  initialUserType?: DemoUserType
}

const parseDemoUserParam = (value: string | null): DemoUserType | undefined => {
  if (value === 'advertiser' || value === 'publisher') return value
  return undefined
}

const DEMO_SOURCE_BY_LOCATION: Partial<Record<TrackingLocationValue, DemoSource>> = {
  [TRACKING_LOCATIONS.LANDING_PAGE]: 'page',
  [TRACKING_LOCATIONS.PAGE_CONTENT]: 'inline',
  [TRACKING_LOCATIONS.MODAL]: 'popup',
}

type DemoFormData = {
  userType: '' | 'advertiser' | 'publisher'
  firstName: string
  lastName: string
  email: string
  companyName: string
  website: string
  budget: string
  subscriberRange: string
}

const budgetOptions = [
  { value: 'Less than $1,000', label: 'Less than $1,000' },
  { value: '$1,000 - $5,000', label: '$1,000 - $5,000' },
  { value: '$5,000 - $10,000', label: '$5,000 - $10,000' },
  { value: '$10,000 - $25,000', label: '$10,000 - $25,000' },
  { value: '$25,000+', label: '$25,000+' },
]

const subscriberRangeOptions = [
  { value: 'Less than 5,000', label: 'Less than 5,000' },
  { value: '5,000 to 20,000', label: '5,000 to 20,000' },
  { value: '20,000 to 50,000', label: '20,000 to 50,000' },
  { value: '50,000 to 100,000', label: '50,000 to 100,000' },
  { value: '100,000+', label: '100,000+' },
]

export const DemoRequestFormFromUrl = (props: Omit<DemoRequestFormProps, 'initialUserType'>) => {
  const searchParams = useSearchParams()
  const initialUserType = parseDemoUserParam(searchParams.get('user'))

  return <DemoRequestForm {...props} initialUserType={initialUserType} />
}

export const DemoRequestForm = ({
  tracking,
  className,
  displayMode,
  intro,
  hideFormHeader = false,
  initialUserType,
}: DemoRequestFormProps) => {
  const turnstileRef = useRef<TurnstileInstance>(null)
  const captchaSiteKey = process.env.NEXT_PUBLIC_CF_CAPTCHA_SITE_KEY
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string>('')

  useEffect(() => {
    setIsCaptchaEnabled(isProductionDomain())
  }, [])

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<DemoFormData>({
    mode: 'onSubmit',
    defaultValues: {
      userType: initialUserType ?? 'advertiser',
      firstName: '',
      lastName: '',
      email: '',
      companyName: '',
      website: '',
      budget: '',
      subscriberRange: '',
    },
  })

  const selectedUserType = watch('userType')

  useEffect(() => {
    if (selectedUserType === 'advertiser') {
      setValue('subscriberRange', '')
    }
    if (selectedUserType === 'publisher') {
      setValue('budget', '')
    }
  }, [selectedUserType, setValue])

  const resetCaptcha = () => {
    setCaptchaToken('')
    turnstileRef.current?.reset()
  }

  const onSubmit = async (data: DemoFormData) => {
    setSubmitError('')

    try {
      // The Rails endpoint normally reads attribution from a signed cookie set
      // by ApplicationController#gtm_cookie. This form posts via a Next.js
      // server action, so the browser cookie never reaches Rails — forward the
      // captured params in the payload instead.
      const attribution = getStoredParams()
      const demoSource = DEMO_SOURCE_BY_LOCATION[tracking.location]

      const payload = {
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        email: data.email.toLowerCase().trim(),
        company_name: data.companyName.trim(),
        url: data.website.trim(),
        account_type: data.userType as 'advertiser' | 'publisher',
        ...(demoSource && { demo_source: demoSource }),
        ...(captchaToken && { cf_turnstile_response: captchaToken }),
        ...(data.userType === 'publisher'
          ? { subscriber_range: data.subscriberRange }
          : { budget: data.budget }),
        ...attribution,
      }

      const result = await submitDemoRequest(payload)

      if (result.success) {
        trackSegmentEvent('demo_requested')
        dataLayerPush('demo_requested')
        setIsSubmitted(true)
        reset()
        resetCaptcha()
      } else {
        setSubmitError(result.error || 'Failed to submit form. Please try again.')
        resetCaptcha()
      }
    } catch {
      setSubmitError('Failed to submit form. Please try again.')
      resetCaptcha()
    }
  }

  return (
    <div
      className={cn(
        'w-full mx-auto',
        displayMode === 'page' ? 'max-w-[620px]' : 'max-w-[480px]',
        className,
      )}
    >
      {isSubmitted ? (
        <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
          <div className="mb-6">
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M32.9096 10.5361C36.2967 5.50297 43.7033 5.50297 47.0904 10.5361C49.0088 13.3869 52.4481 14.8115 55.8204 14.1522C61.7745 12.9883 67.0117 18.2255 65.8478 24.1796C65.1885 27.5519 66.6131 30.9912 69.4639 32.9096C74.497 36.2967 74.497 43.7033 69.4639 47.0904C66.6131 49.0088 65.1885 52.4481 65.8478 55.8204C67.0117 61.7745 61.7745 67.0117 55.8204 65.8478C52.4481 65.1885 49.0088 66.6131 47.0904 69.4639C43.7033 74.497 36.2967 74.497 32.9096 69.4639C30.9912 66.6131 27.5519 65.1885 24.1796 65.8478C18.2255 67.0117 12.9883 61.7745 14.1522 55.8204C14.8115 52.4481 13.3869 49.0088 10.5361 47.0904C5.50297 43.7033 5.50297 36.2967 10.5361 32.9096C13.3869 30.9912 14.8115 27.5519 14.1522 24.1796C12.9883 18.2255 18.2255 12.9883 24.1796 14.1522C27.5519 14.8115 30.9912 13.3869 32.9096 10.5361Z"
                fill="#2EA568"
              />
              <path
                d="M31.2197 40.4878L38.2929 48.5366L52.6831 31.7073"
                stroke="white"
                strokeWidth="2.43902"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="mb-4 font-heading text-[48px] leading-[1.1] tracking-[-0.02em] text-white-950">
            Demo request sent
          </h2>
          <p className="max-w-[430px] size-body-md text-white-800">
            We&apos;ve received your request, someone from the Paved team will be in touch shortly.
          </p>
        </div>
      ) : (
        <div>
          {intro && intro.length > 0 && (
            <div className="mb-6xl">
              <CustomPortableText value={intro} tracking={tracking} />
            </div>
          )}
          {!hideFormHeader && (
            <h2 className="size-header-h3 font-normal text-white-950 mb-6xl">Request a demo</h2>
          )}
          <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            className={cn('flex flex-col gap-6xl')}
          >
            <div className="flex flex-col gap-lg">
              <Controller
                control={control}
                name="userType"
                rules={{
                  required: 'Please select your user type',
                }}
                render={({ field }) => (
                  <RadioGroup
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.userType?.message}
                    disabled={isSubmitting}
                    options={[
                      { label: 'Advertiser', value: 'advertiser' },
                      { label: 'Publisher', value: 'publisher' },
                    ]}
                  />
                )}
              />
              <div className="grid grid-cols-1 gap-xl md:grid-cols-2">
                <Input
                  {...register('firstName', {
                    required: 'First name is required',
                  })}
                  type="text"
                  maxLength={30}
                  error={errors.firstName?.message}
                  disabled={isSubmitting}
                  placeholder="First name"
                  variant="small"
                />
                <Input
                  {...register('lastName', {
                    required: 'Last name is required',
                  })}
                  type="text"
                  maxLength={30}
                  error={errors.lastName?.message}
                  disabled={isSubmitting}
                  placeholder="Last name"
                  variant="small"
                />
                <Input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address',
                    },
                    validate: (value) =>
                      selectedUserType !== 'advertiser' ||
                      !value ||
                      isCompanyEmail(value) ||
                      'Work email required.',
                  })}
                  type="email"
                  maxLength={100}
                  error={errors.email?.message}
                  disabled={isSubmitting}
                  placeholder="Email"
                  variant="small"
                />
                <Input
                  {...register('companyName', {
                    required: 'Company name is required',
                  })}
                  type="text"
                  maxLength={50}
                  error={errors.companyName?.message}
                  disabled={isSubmitting}
                  placeholder="Company name"
                  variant="small"
                />
              </div>
              <Input
                {...register('website', {
                  required: 'Website is required',
                  pattern: {
                    value: /^((http(s)?:\/\/)?([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-z]{2,})(\S*)?$/,
                    message: 'Please enter a valid website URL',
                  },
                })}
                type="text"
                maxLength={75}
                error={errors.website?.message}
                disabled={isSubmitting}
                placeholder="Website"
                variant="small"
              />
              <Controller
                control={control}
                name={selectedUserType === 'publisher' ? 'subscriberRange' : 'budget'}
                rules={{
                  validate: (value) => {
                    if (selectedUserType === 'publisher' && !value) {
                      return 'Number of subscribers is required'
                    }
                    if (selectedUserType === 'advertiser' && !value) {
                      return 'Campaign budget is required'
                    }
                    return true
                  },
                }}
                render={({ field }) => (
                  <Select
                    items={
                      selectedUserType === 'publisher' ? subscriberRangeOptions : budgetOptions
                    }
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isSubmitting}
                    className="max-w-[240px]"
                    placeholder={
                      selectedUserType === 'publisher'
                        ? 'Number of US subscribers'
                        : 'Campaign budget'
                    }
                    error={
                      selectedUserType === 'publisher'
                        ? errors.subscriberRange?.message
                        : errors.budget?.message
                    }
                    portal={displayMode === 'modal'}
                    variant="small"
                  />
                )}
              />
            </div>

            {isCaptchaEnabled && captchaSiteKey && (
              <Turnstile
                ref={turnstileRef}
                siteKey={captchaSiteKey}
                onSuccess={setCaptchaToken}
                onError={() => setCaptchaToken('')}
                onExpire={() => setCaptchaToken('')}
                options={{
                  theme: 'light',
                  size: 'flexible',
                  appearance: 'interaction-only',
                }}
              />
            )}

            {submitError && <p className="size-body-sm text-red-500 font-medium">{submitError}</p>}

            <div className="flex justify-center md:justify-start">
              <Button
                button={{
                  _type: 'button',
                  variant: 'primaryDark',
                  size: 'md',
                  link: undefined,
                  icon: undefined,
                  iconPlacement: 'none',
                  dataId: null,
                }}
                className={buttonSizeClasses({ lg: 'md' })}
                tracking={tracking}
                type="submit"
                disabled={isSubmitting || (isCaptchaEnabled && !captchaToken)}
              >
                {isSubmitting ? 'Submitting...' : 'Request a demo'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
